import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { INSURANCE_TYPES, US_STATES } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOTAL_STEPS = 7;

const initialState = {
  insurance_type: "",
  coverage_amount: 500000,
  term_length: 20,
  date_of_birth: "",
  gender: "",
  state: "",
  zip_code: "",
  marital_status: "",
  height_inches: 68,
  weight_lbs: 170,
  tobacco_use: "",
  health_status: "",
  annual_income: 75000,
  occupation: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  consent: true,
};

export default function QuoteWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = params.get("type");
    if (t && INSURANCE_TYPES.find((x) => x.id === t)) {
      setForm((f) => ({ ...f, insurance_type: t }));
      setStep(2);
    }
  }, [params]);

  const showTerm = useMemo(
    () => ["term", "return_of_premium"].includes(form.insurance_type),
    [form.insurance_type]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canContinue = () => {
    switch (step) {
      case 1: return !!form.insurance_type;
      case 2: return form.coverage_amount >= 25000 && (!showTerm || form.term_length >= 10);
      case 3: return !!form.date_of_birth && !!form.gender && !!form.state && form.zip_code.length >= 5;
      case 4: return !!form.tobacco_use && !!form.health_status;
      case 5: return form.annual_income >= 0;
      case 6: return form.first_name && form.last_name && form.email && form.phone && form.consent;
      default: return true;
    }
  };

  const next = () => {
    if (!canContinue()) {
      toast.error("Please complete the required fields to continue.");
      return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!showTerm) delete payload.term_length;
      const res = await axios.post(`${API}/quotes`, payload);
      const q = res.data;
      navigate(
        `/thank-you?id=${q.id}&low=${q.estimated_monthly_low}&high=${q.estimated_monthly_high}&type=${q.insurance_type}&coverage=${q.coverage_amount}`
      );
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please review your details and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const progressValue = (step / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-pp-bone pt-28 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs uppercase tracking-[0.2em] text-pp-forest font-bold">
              Step {step} of {TOTAL_STEPS}
            </span>
            <span className="text-xs text-pp-text-secondary flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 stroke-[1.5]" />
              Secure & confidential
            </span>
          </div>
          <Progress value={progressValue} className="h-1.5 bg-pp-cream" data-testid="quote-progress" />
        </div>

        <div
          className="bg-white border border-pp-border-subtle rounded-3xl p-8 sm:p-12 shadow-[0_12px_40px_rgba(26,46,38,0.04)]"
          data-testid={`quote-step-${step}`}
        >
          {step === 1 && (
            <StepWrapper
              title="What type of life insurance are you considering?"
              subtitle="Not sure? Pick the closest fit—Darryl will help refine on your call."
            >
              <div className="grid sm:grid-cols-2 gap-4">
                {INSURANCE_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`quote-type-${t.id}`}
                    onClick={() => set("insurance_type", t.id)}
                    className={`text-left rounded-2xl border-2 p-6 transition-all ${
                      form.insurance_type === t.id
                        ? "border-pp-forest bg-pp-cream"
                        : "border-pp-border-subtle hover:border-pp-forest/40 bg-white"
                    }`}
                  >
                    <div className="font-serif text-xl text-pp-forest-deep mb-1">
                      {t.name}
                    </div>
                    <div className="text-sm text-pp-text-secondary">
                      {t.short}
                    </div>
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="quote-type-not-sure"
                  onClick={() => set("insurance_type", "term")}
                  className={`text-left rounded-2xl border-2 p-6 transition-all border-pp-border-subtle hover:border-pp-forest/40 bg-white`}
                >
                  <div className="font-serif text-xl text-pp-forest-deep mb-1">
                    Not sure yet
                  </div>
                  <div className="text-sm text-pp-text-secondary">
                    We'll start with Term (most common) and adjust later.
                  </div>
                </button>
              </div>
            </StepWrapper>
          )}

          {step === 2 && (
            <StepWrapper
              title="How much coverage and for how long?"
              subtitle="A common rule: 10–15× your annual income."
            >
              <div className="space-y-10">
                <div>
                  <div className="flex justify-between items-end mb-3">
                    <Label className="text-pp-forest-deep font-medium">
                      Coverage amount
                    </Label>
                    <span className="font-serif text-3xl text-pp-forest-deep">
                      ${form.coverage_amount.toLocaleString()}
                    </span>
                  </div>
                  <Slider
                    data-testid="quote-coverage-slider"
                    value={[form.coverage_amount]}
                    min={25000}
                    max={3000000}
                    step={25000}
                    onValueChange={(v) => set("coverage_amount", v[0])}
                  />
                  <div className="flex justify-between text-xs text-pp-text-secondary mt-2">
                    <span>$25K</span>
                    <span>$3M</span>
                  </div>
                </div>
                {showTerm && (
                  <div>
                    <Label className="text-pp-forest-deep font-medium mb-3 block">
                      Term length
                    </Label>
                    <div className="grid grid-cols-4 gap-3">
                      {[10, 15, 20, 30].map((y) => (
                        <button
                          key={y}
                          type="button"
                          data-testid={`quote-term-${y}`}
                          onClick={() => set("term_length", y)}
                          className={`py-4 rounded-xl border-2 font-medium transition-all ${
                            form.term_length === y
                              ? "border-pp-forest bg-pp-cream text-pp-forest-deep"
                              : "border-pp-border-subtle hover:border-pp-forest/40 text-pp-text-secondary"
                          }`}
                        >
                          {y} yrs
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </StepWrapper>
          )}

          {step === 3 && (
            <StepWrapper
              title="A few quick details about you"
              subtitle="Used only to estimate your rate. Not shared."
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Date of birth">
                  <Input
                    data-testid="quote-dob"
                    type="date"
                    value={form.date_of_birth}
                    onChange={(e) => set("date_of_birth", e.target.value)}
                  />
                </Field>
                <Field label="Gender (per carrier underwriting)">
                  <Select
                    value={form.gender}
                    onValueChange={(v) => set("gender", v)}
                  >
                    <SelectTrigger data-testid="quote-gender">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="State">
                  <Select
                    value={form.state}
                    onValueChange={(v) => set("state", v)}
                  >
                    <SelectTrigger data-testid="quote-state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {US_STATES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="ZIP code">
                  <Input
                    data-testid="quote-zip"
                    inputMode="numeric"
                    maxLength={5}
                    value={form.zip_code}
                    onChange={(e) => set("zip_code", e.target.value.replace(/\D/g, "").slice(0, 5))}
                    placeholder="e.g. 30303"
                  />
                </Field>
                <Field label="Marital status">
                  <Select
                    value={form.marital_status}
                    onValueChange={(v) => set("marital_status", v)}
                  >
                    <SelectTrigger data-testid="quote-marital">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </StepWrapper>
          )}

          {step === 4 && (
            <StepWrapper
              title="Tell us about your health"
              subtitle="Honest answers help us find the most accurate rate."
            >
              <div className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={`Height: ${Math.floor(form.height_inches / 12)}'${form.height_inches % 12}"`}>
                    <Slider
                      data-testid="quote-height"
                      value={[form.height_inches]}
                      min={48}
                      max={84}
                      step={1}
                      onValueChange={(v) => set("height_inches", v[0])}
                    />
                  </Field>
                  <Field label={`Weight: ${form.weight_lbs} lbs`}>
                    <Slider
                      data-testid="quote-weight"
                      value={[form.weight_lbs]}
                      min={80}
                      max={400}
                      step={1}
                      onValueChange={(v) => set("weight_lbs", v[0])}
                    />
                  </Field>
                </div>

                <div>
                  <Label className="text-pp-forest-deep font-medium mb-3 block">
                    Tobacco / nicotine use
                  </Label>
                  <RadioGroup
                    value={form.tobacco_use}
                    onValueChange={(v) => set("tobacco_use", v)}
                    className="grid sm:grid-cols-3 gap-3"
                  >
                    {[
                      { v: "never", label: "Never" },
                      { v: "former", label: "Former (12+ mo)" },
                      { v: "current", label: "Current" },
                    ].map((o) => (
                      <Label
                        key={o.v}
                        htmlFor={`tob-${o.v}`}
                        data-testid={`quote-tobacco-${o.v}`}
                        className={`flex items-center gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all ${
                          form.tobacco_use === o.v
                            ? "border-pp-forest bg-pp-cream"
                            : "border-pp-border-subtle hover:border-pp-forest/40"
                        }`}
                      >
                        <RadioGroupItem value={o.v} id={`tob-${o.v}`} />
                        <span className="text-pp-forest-deep text-sm font-medium">
                          {o.label}
                        </span>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="text-pp-forest-deep font-medium mb-3 block">
                    Overall health
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {[
                      { v: "excellent", label: "Excellent" },
                      { v: "very_good", label: "Very good" },
                      { v: "good", label: "Good" },
                      { v: "average", label: "Average" },
                      { v: "poor", label: "Poor" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        data-testid={`quote-health-${o.v}`}
                        onClick={() => set("health_status", o.v)}
                        className={`py-3 px-2 rounded-xl border-2 text-sm font-medium transition-all ${
                          form.health_status === o.v
                            ? "border-pp-forest bg-pp-cream text-pp-forest-deep"
                            : "border-pp-border-subtle hover:border-pp-forest/40 text-pp-text-secondary"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </StepWrapper>
          )}

          {step === 5 && (
            <StepWrapper
              title="A bit about your finances"
              subtitle="Helps us right-size coverage to replace your income."
            >
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-end mb-3">
                    <Label className="text-pp-forest-deep font-medium">
                      Annual household income
                    </Label>
                    <span className="font-serif text-2xl text-pp-forest-deep">
                      ${form.annual_income.toLocaleString()}
                    </span>
                  </div>
                  <Slider
                    data-testid="quote-income"
                    value={[form.annual_income]}
                    min={0}
                    max={500000}
                    step={5000}
                    onValueChange={(v) => set("annual_income", v[0])}
                  />
                </div>
                <Field label="Occupation (optional)">
                  <Input
                    data-testid="quote-occupation"
                    value={form.occupation}
                    onChange={(e) => set("occupation", e.target.value)}
                    placeholder="e.g. Teacher, Engineer, Self-employed"
                  />
                </Field>
              </div>
            </StepWrapper>
          )}

          {step === 6 && (
            <StepWrapper
              title="Where should Darryl send your quote?"
              subtitle="No spam. No call centers. One licensed broker, that's it."
            >
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="First name">
                    <Input
                      data-testid="quote-first-name"
                      value={form.first_name}
                      onChange={(e) => set("first_name", e.target.value)}
                    />
                  </Field>
                  <Field label="Last name">
                    <Input
                      data-testid="quote-last-name"
                      value={form.last_name}
                      onChange={(e) => set("last_name", e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Email">
                  <Input
                    data-testid="quote-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    data-testid="quote-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </Field>
                <Label
                  className="flex items-start gap-3 mt-4 cursor-pointer"
                  htmlFor="consent"
                >
                  <Checkbox
                    id="consent"
                    data-testid="quote-consent"
                    checked={form.consent}
                    onCheckedChange={(v) => set("consent", !!v)}
                  />
                  <span className="text-xs text-pp-text-secondary leading-relaxed">
                    I agree to be contacted by Darryl Kelly at Policy Provisions
                    regarding my quote via email or phone. I understand this
                    is not a binding offer of coverage.
                  </span>
                </Label>
              </div>
            </StepWrapper>
          )}

          {step === 7 && (
            <StepWrapper
              title="Review and submit"
              subtitle="Double-check the highlights before we generate your estimate."
            >
              <ReviewSummary form={form} showTerm={showTerm} />
            </StepWrapper>
          )}

          <div className="mt-10 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={back}
              disabled={step === 1}
              data-testid="quote-back-btn"
              className="text-pp-forest hover:bg-pp-cream rounded-full px-6"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>

            {step < TOTAL_STEPS ? (
              <Button
                type="button"
                onClick={next}
                data-testid="quote-next-btn"
                className="bg-pp-terracotta hover:bg-pp-terracotta-dark text-white rounded-full px-8 py-5"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="quote-submit-btn"
                className="bg-pp-forest hover:bg-pp-forest-dark text-white rounded-full px-10 py-5"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>Get my estimate</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepWrapper({ title, subtitle, children }) {
  return (
    <div className="space-y-7">
      <div>
        <h2 className="font-serif text-3xl sm:text-4xl text-pp-forest-deep leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-3 text-pp-text-secondary">{subtitle}</p>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-pp-forest-deep font-medium mb-2 block text-sm">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ReviewSummary({ form, showTerm }) {
  const type =
    INSURANCE_TYPES.find((t) => t.id === form.insurance_type)?.name || "—";
  const rows = [
    ["Insurance type", type],
    ["Coverage", `$${form.coverage_amount.toLocaleString()}`],
    ...(showTerm ? [["Term length", `${form.term_length} years`]] : []),
    ["Date of birth", form.date_of_birth || "—"],
    ["Gender", form.gender || "—"],
    ["State / ZIP", `${form.state || "—"} ${form.zip_code || ""}`],
    [
      "Height / Weight",
      `${Math.floor(form.height_inches / 12)}'${form.height_inches % 12}" / ${form.weight_lbs} lbs`,
    ],
    ["Tobacco use", form.tobacco_use || "—"],
    ["Overall health", form.health_status?.replace("_", " ") || "—"],
    ["Annual income", `$${form.annual_income.toLocaleString()}`],
    ["Name", `${form.first_name} ${form.last_name}`],
    ["Contact", `${form.email} • ${form.phone}`],
  ];
  return (
    <div className="bg-pp-cream rounded-2xl p-6 sm:p-8 divide-y divide-pp-border-subtle">
      {rows.map(([k, v]) => (
        <div
          key={k}
          className="flex justify-between gap-4 py-3 text-sm first:pt-0 last:pb-0"
        >
          <span className="text-pp-text-secondary">{k}</span>
          <span className="text-pp-forest-deep font-medium text-right">
            {v}
          </span>
        </div>
      ))}
    </div>
  );
}
